import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ICalendarEvent } from '../Interfaces/Calendar/ICalendarEvent';
import { customFetcher } from './useFetch';

export const useGetCalendarEvents = (userId: number) => {
  const {
    data,
    isFetching: isFetchingCalendarEvents,
    isError: isErrorCalendarEvents,
    refetch: refetchCalendarEvents,
  } = useQuery<ICalendarEvent[]>({
    refetchOnWindowFocus: false,
    queryKey: ['calendarEvents', userId],
    retry: false,
    queryFn: () =>
      customFetcher({
        endpoint: `/calendar/events/${userId}`,
      }),
  });

  return {
    calendarEvents: data,
    isFetchingCalendarEvents,
    isErrorCalendarEvents,
    refetchCalendarEvents,
  };
};

export const useCreateCalendarEvent = (userId: number) => {
  const queryClient = useQueryClient();
  const {
    mutateAsync: createCalendarEvent,
    isPending: isPendingCreateCalendarEvent,
  } = useMutation({
    mutationFn: (event: ICalendarEvent) =>
      customFetcher({
        endpoint: `/calendar/events/${userId}`,
        method: 'POST',
        body: event,
      }),
    // onSuccess wird aufgerufen, wenn die Mutation erfolgreich war
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['calendarEvents'] });
    },
  });

  return { createCalendarEvent, isPendingCreateCalendarEvent };
};

export const useUpdateCalendarEvent = (userId: number) => {
  const queryClient = useQueryClient();
  const {
    mutateAsync: updateCalendarEvent,
    isPending: isPendingUpdateCalendarEvent,
  } = useMutation({
    mutationFn: (event: ICalendarEvent) =>
      customFetcher({
        endpoint: `/calendar/events/${userId}`,
        method: 'PUT',
        body: event,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['calendarEvents'] });
    },
  });

  return { updateCalendarEvent, isPendingUpdateCalendarEvent };
};

export const useDeleteCalendarEvent = (userId: number) => {
  const queryClient = useQueryClient();
  const {
    mutateAsync: deleteCalendarEvent,
    isPending: isPendingDeleteCalendarEvent,
  } = useMutation({
    mutationFn: (event: ICalendarEvent) =>
      customFetcher({
        endpoint: `/calendar/events/${userId}`,
        method: 'DELETE',
        body: event,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['calendarEvents'] });
    },
  });

  return {
    deleteCalendarEvent,
    isPendingDeleteCalendarEvent,
  };
};
